import { useMemo, useState } from 'react'
import Card from '@/components/ui/Card'
import { useAuth } from '@/lib/auth-context'

export default function ProfileShare() {
  const { session, profile } = useAuth()
  const token = session?.access_token || ''
  const teacherId = profile?.id || null
  const [shareUrl, setShareUrl] = useState('')
  const [shareCode, setShareCode] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const hasLink = Boolean(shareUrl)

  const shareLabel = useMemo(() => {
    if (!shareUrl) return 'Your link will appear here after you generate it.'
    return shareUrl
  }, [shareUrl])

  const handleCreate = async () => {
    if (!token || !teacherId) {
      setErrorMessage('We could not verify your session. Please sign in again.')
      return
    }
    setLoading(true)
    setErrorMessage('')
    setStatusMessage('')
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/teacher/shares`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teacher_id: teacherId, note: 'Teacher-generated share link' }),
      })
      const raw = await response.json().catch(() => ({}))
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
        throw new Error(errMsg || 'Unable to create a share link right now')
      }
      const data = raw?.data ?? raw
      const url = data?.url || ''
      const code = data?.code || ''
      setShareUrl(url)
      setShareCode(code)
      if (url) {
        try {
          await navigator.clipboard?.writeText?.(url)
          setStatusMessage('New share link created and copied to your clipboard!')
        } catch (_err) {
          setStatusMessage('New share link created. Copy it below to share with sponsors.')
        }
      } else {
        setStatusMessage('New share link created.')
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to create a share link right now')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard?.writeText?.(shareUrl)
      setStatusMessage('Share link copied to clipboard!')
      setErrorMessage('')
    } catch (_err) {
      window.prompt('Copy this share link', shareUrl)
      setStatusMessage('Share link ready to copy.')
    }
  }

  const handleRevoke = async () => {
    if (!token || !teacherId) {
      setErrorMessage('We could not verify your session. Please sign in again.')
      return
    }
    const defaultCode = shareCode || (shareUrl ? shareUrl.split('/').pop() : '')
    const input = window.prompt('Enter the share code you want to revoke', defaultCode || '')
    if (!input) return
    setLoading(true)
    setErrorMessage('')
    setStatusMessage('')
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const response = await fetch(`${CORE_API}/api/v1/teacher/shares/revoke`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ teacher_id: teacherId, code: String(input).trim() }),
      })
      const raw = await response.json().catch(() => ({}))
      if (!response.ok) {
        const errMsg = typeof raw?.error === 'string' ? raw.error : raw?.error?.message
        throw new Error(errMsg || 'Unable to revoke that share link')
      }
      setStatusMessage('Share link revoked. Generate a new one when you are ready to share again.')
      if (shareCode && shareCode === String(input).trim()) {
        setShareUrl('')
        setShareCode('')
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to revoke that share link')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="space-y-4 bg-white p-5">
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-cocoa/60">Teacher Share Links</p>
        <h2 className="text-xl font-semibold text-cocoa">Share your Project Tower profile</h2>
        <p className="text-sm text-cocoa/70">
          Generate a short link you can share with sponsors, donors, and partners. You can rotate or revoke links at any time.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn" onClick={handleCreate} disabled={loading}>
          {loading ? 'Working…' : hasLink ? 'Rotate Link' : 'Generate Link'}
        </button>
        <button type="button" className="btn bg-white text-cocoa hover:bg-shell" onClick={handleCopy} disabled={!hasLink}>
          Copy Link
        </button>
        <button
          type="button"
          className="btn border border-brick bg-white text-brick hover:bg-brick/10"
          onClick={handleRevoke}
          disabled={loading}
        >
          Revoke Link
        </button>
      </div>

      <div className="rounded-xl border border-sand bg-shell/40 p-4 text-sm">
        <div className="font-semibold text-cocoa">Current share link</div>
        <div className="mt-1 break-all font-mono text-xs text-cocoa/80">{shareLabel}</div>
        <div className="mt-2 text-xs text-cocoa/60">
          Tip: share this URL directly or include the code <span className="font-semibold">{shareCode || '—'}</span> in emails.
        </div>
      </div>

      {statusMessage ? <p className="text-sm text-mint">{statusMessage}</p> : null}
      {errorMessage ? <p className="text-sm text-brick">{errorMessage}</p> : null}
    </Card>
  )
}
